import { useState } from "react";
import { Check, Languages } from "lucide-react";

import {
  LOCALE_DEFINITIONS,
  type InstallableLocale,
} from "../i18n/preparedLocales";
import { useDismissibleLayer } from "./useDismissibleLayer";
import "./theme-preference-control.css";

export function LanguagePreferenceControl({
  activeLocale,
  locales,
  label,
  onSelect,
  busy = false,
  error = null,
  variant = "menu",
  id,
}: {
  activeLocale: InstallableLocale;
  locales: readonly InstallableLocale[];
  label: string;
  onSelect: (locale: InstallableLocale) => void;
  busy?: boolean;
  error?: string | null;
  variant?: "menu" | "field";
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const layerRef = useDismissibleLayer<HTMLDivElement>(open, () => setOpen(false));
  const uniqueLocales = [...new Set(locales)];

  if (variant === "field") return <div className="kavoosh-preference-field"><Languages size={17} aria-hidden="true" /><select id={id} aria-label={label} value={activeLocale} disabled={busy || !uniqueLocales.length} onChange={event => onSelect(event.currentTarget.value as InstallableLocale)}>
    {(uniqueLocales.length ? uniqueLocales : [activeLocale]).map(locale => {
      const definition = LOCALE_DEFINITIONS[locale];
      return <option key={locale} value={locale} dir={definition.direction}>{definition.label}{definition.label !== definition.englishLabel ? ` · ${definition.englishLabel}` : ""}</option>;
    })}
  </select></div>;

  return <div ref={layerRef} className="kavoosh-preference-control">
    <button
      type="button"
      className="kavoosh-preference-trigger"
      aria-label={label}
      title={label}
      aria-haspopup="menu"
      aria-expanded={open}
      disabled={busy}
      onClick={() => setOpen((value) => !value)}
    >
      <Languages size={18} aria-hidden="true" />
      <span className="kavoosh-preference-locale-code" aria-hidden="true">
        {activeLocale.toUpperCase()}
      </span>
    </button>
    {open ? <div className="kavoosh-preference-menu is-language" role="menu" aria-label={label}>
      {error ? <p className="kavoosh-preference-error" role="alert">{error}</p> : null}
      {uniqueLocales.map((locale) => {
        const definition = LOCALE_DEFINITIONS[locale];
        return <button
          key={locale}
          type="button"
          role="menuitemradio"
          aria-checked={activeLocale === locale}
          dir={definition.direction}
          disabled={busy}
          onClick={() => {
            onSelect(locale);
            setOpen(false);
          }}
        >
          <span className="kavoosh-preference-language">
            <strong>{definition.label}</strong>
            {definition.label !== definition.englishLabel
              ? <small>{definition.englishLabel}</small>
              : null}
          </span>
          {activeLocale === locale ? <Check size={15} aria-hidden="true" /> : null}
        </button>;
      })}
    </div> : null}
  </div>;
}
